import { EngagementDto } from '@families-link/shared';
import { useSnackbar } from 'notistack';
import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationModalProps, ConfirmationModal } from '../../../_core/components/confirmation-modal';
import { useMuiModal } from '../../../_core/hooks';
import { buttonModalWindowStyles } from '../../../_core/styles';
import { Theme } from '@mui/material';
import { ReminderConfirmTable } from '../../components/reminder-error-table/remonder-confirm-table.component';
import {
	ReminderConfirmModal,
	ReminderConfirmModalProps,
} from '../../components/modal-form/reminder-confirm-form.component';

export type useOpenConfirmModalFormHookProps = {
	featchAction(values: EngagementDto | EngagementDto[]): Promise<any>;
	handlePagination?(): void;
	title: string;
	description?: string;
	icon?: ReactNode;
	danger?: boolean;
	info?: boolean;
	submitButtonName?: string;
	toastText?: string;
	customBackgrondColorKey?: keyof Theme['colors'];
	customColorKey?: keyof Theme['colors'];
	customBorderColorKey?: keyof Theme['colors'];
};

export function useOpenRemindersConfirmModal(props: useOpenConfirmModalFormHookProps) {
	const {
		featchAction,
		handlePagination,
		title,
		description,
		icon,
		danger = false,
		info = false,
		submitButtonName,
		toastText,
		customBackgrondColorKey,
		customBorderColorKey,
		customColorKey,
	} = props;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();

	const showToast = (
		message: string | ReactNode,
		variant: 'error' | 'default' | 'success' | 'warning' | 'info' | undefined
	) => {
		enqueueSnackbar(message, {
			variant,
		});
	};

	const { openModal, closeModal } = useMuiModal<ConfirmationModalProps>(ConfirmationModal);

	const { openModal: openErrorsModal, closeModal: closeErrorsModal } =
		useMuiModal<ReminderConfirmModalProps>(ReminderConfirmModal);
	const handleConfirm = async (value: EngagementDto | EngagementDto[]) => {
		try {
			const result = await featchAction(value);

			if (result?.successes?.length > 0) {
				showToast(`${result.successes.length} reminder(s) created successfully`, 'success');
			}

			if (result?.errors?.length > 0) {
				openErrorsModal({
					data: result.errors as any,
					closeModal: closeErrorsModal,
				});
			}
			handlePagination && handlePagination();
			closeModal();
		} catch (error) {
			console.error(error);
		}
	};

	const handleOpenConfirmModal = useCallback(
		(value: EngagementDto | EngagementDto[]) => {
			const handleSubmit = async () => {
				await handleConfirm(value);
			};

			let engagementTitles: string;
			if (Array.isArray(value)) {
				engagementTitles = value
					.map((val) => {
						const titleField = val.customFields?.find((field) => field.name === 'Title');
						return val?.customFieldsValues?.find((e) => e.fieldId.toString() === titleField?._id.toString())?.value;
					})
					.join(', ');
			} else {
				const titleField = value.customFields?.find((field) => field.name === 'Title');
				engagementTitles =
					value?.customFieldsValues?.find((e) => e.fieldId.toString() === titleField?._id.toString())?.value || '';
			}

			openModal({
				title,
				description: description?.replace(':engagementTitle', engagementTitles),
				onSubmit: handleSubmit,
				onClose: closeModal,
				icon,
				customBackgrondColorKey: customBackgrondColorKey,
				customColorKey: customColorKey,
				customBorderColorKey: customBorderColorKey,
				danger,
				leftButtonProps: {
					children: t('engagements.forms.buttons.cancel'),
					variant: 'secondary',
					sx: buttonModalWindowStyles,
				},
				rightButtonProps: {
					children: t('engagements.forms.buttons.confirm'),
					variant: danger ? 'danger' : info ? 'info' : 'primary',
					contraModeStyleButton: true,
					sx: buttonModalWindowStyles,
				},
			});
		},
		[handleConfirm, openModal]
	);

	return handleOpenConfirmModal;
}
